import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Box } from "@mui/material";
import { Alert, Divider, Form, Modal, Space, Switch, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import ActionButton from "../../../components/common/buttons/ActionButton";
import CancelButton from "../../../components/common/buttons/CancelButton";
import NumberField from "../../../components/common/fields/NumberField";
import {
  CancellationPolicy,
  CancellationPolicyRequest,
  CancellationPolicyTier,
} from "../../../@type/billing.types";

interface Props {
  open: boolean;
  policy: CancellationPolicy | null;
  saving: boolean;
  onCancel: () => void;
  onSave: (request: CancellationPolicyRequest) => void;
}

const DEFAULT_POLICY: CancellationPolicyRequest = {
  is_active: true,
  no_show_fee_percent: 100,
  refund_to_credit_allowed: false,
  platform_fee_share_percent: 50,
  owner_cancel_min_hours: 3,
  tiers: [
    { min_hours_before_start: 24, fee_percent: 0 },
    { min_hours_before_start: 12, fee_percent: 20 },
    { min_hours_before_start: 3, fee_percent: 50 },
    { min_hours_before_start: 0, fee_percent: 100 },
  ],
};

/** Same rules as the server: a 0h tier, unique hours, fee never drops closer to the start. */
export const validateTiers = (tiers: CancellationPolicyTier[]): string | null => {
  if (!tiers.length) return "At least one tier is required";
  const hours = tiers.map((tier) => tier.min_hours_before_start);
  if (new Set(hours).size !== hours.length) return "Duplicate hours in tiers";
  if (!hours.includes(0)) return "A tier with 0 hours is required";
  const sorted = [...tiers].sort((a, b) => b.min_hours_before_start - a.min_hours_before_start);
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i].fee_percent < sorted[i - 1].fee_percent) {
      return "Fee must not decrease as the cancellation gets closer to the start";
    }
  }
  return null;
};

/** Mirrors CancellationPolicyService.calculate for a quick what-if. */
export const previewFee = (
  tiers: CancellationPolicyTier[],
  hoursBefore: number,
  paidAmount: number,
  tax: number
) => {
  const sorted = [...tiers].sort((a, b) => b.min_hours_before_start - a.min_hours_before_start);
  const tier =
    sorted.find((item) => item.min_hours_before_start <= hoursBefore) ?? sorted[sorted.length - 1];
  const feePercent = tier ? tier.fee_percent : 0;
  const feeBase = Math.max(paidAmount - tax, 0);
  const fee = Math.min(Math.round(((feeBase * feePercent) / 100) * 100) / 100, paidAmount);
  return { feePercent, fee, refund: paidAmount - fee };
};

const CancellationPolicyEditor = ({ open, policy, saving, onCancel, onSave }: Props) => {
  const [form] = Form.useForm<CancellationPolicyRequest>();
  const [previewHours, setPreviewHours] = useState(6);
  const [previewPaid, setPreviewPaid] = useState(100);
  const [previewTax, setPreviewTax] = useState(0);
  const tiers = Form.useWatch("tiers", form) ?? [];

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue(policy ? { ...policy } : DEFAULT_POLICY);
  }, [open, policy, form]);

  const tierError = useMemo(() => validateTiers(tiers), [tiers]);
  const preview = useMemo(
    () => previewFee(tiers, previewHours, previewPaid, previewTax),
    [tiers, previewHours, previewPaid, previewTax]
  );

  const handleOk = async () => {
    const values = await form.validateFields();
    if (validateTiers(values.tiers)) return;
    onSave(values);
  };

  return (
    <Modal
      title={`Cancellation policy - ${policy ? policy.country_name : "new"}`}
      open={open}
      onCancel={onCancel}
      footer={
        <Box display="flex" justifyContent="flex-end" gap={1.5}>
          <CancelButton onClick={onCancel}>Cancel</CancelButton>
          <ActionButton onClick={handleOk} loading={saving} disabled={Boolean(tierError)}>
            Save
          </ActionButton>
        </Box>
      }
      width={640}
    >
      <Form form={form} layout="vertical" initialValues={DEFAULT_POLICY}>
        <Space size="large" wrap>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch checkedChildren="ON" unCheckedChildren="OFF" />
          </Form.Item>
          <Form.Item name="refund_to_credit_allowed" label="Allow refund to credit" valuePropName="checked">
            <Switch checkedChildren="ON" unCheckedChildren="OFF" />
          </Form.Item>
        </Space>
        <Space size="large" wrap>
          <Form.Item name="no_show_fee_percent" label="No-show fee" rules={[{ required: true }]}>
            <NumberField min={0} max={100} precision={2} suffix="%" width={160} />
          </Form.Item>
          <Form.Item name="platform_fee_share_percent" label="NailsMap share of fee" rules={[{ required: true }]}>
            <NumberField min={0} max={100} precision={2} suffix="%" width={160} />
          </Form.Item>
          <Form.Item name="owner_cancel_min_hours" label="Salon can cancel up to (hours before)" rules={[{ required: true }]}>
            <NumberField min={0} precision={0} suffix="h" width={160} />
          </Form.Item>
        </Space>

        <Divider orientation="left">Fee tiers (cancel at least N hours before start)</Divider>
        <Form.List name="tiers">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => (
                <Space key={field.key} align="baseline" style={{ display: "flex" }}>
                  <Form.Item name={[field.name, "min_hours_before_start"]} rules={[{ required: true }]}>
                    <NumberField min={0} precision={0} prefix="From" suffix="h" width={170} />
                  </Form.Item>
                  <Form.Item name={[field.name, "fee_percent"]} rules={[{ required: true }]}>
                    <NumberField min={0} max={100} precision={2} prefix="Fee" suffix="%" width={170} />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(field.name)} />
                </Space>
              ))}
              <CancelButton startIcon={<PlusOutlined />} onClick={() => add({ min_hours_before_start: 0, fee_percent: 100 })}>
                Add tier
              </CancelButton>
            </>
          )}
        </Form.List>
        {tierError && <Alert style={{ marginTop: 12 }} type="error" showIcon message={tierError} />}
      </Form>

      <Divider orientation="left">Preview</Divider>
      <Space wrap>
        <NumberField value={previewHours} min={-24} onChange={(v) => setPreviewHours(v ?? 0)} prefix="Cancel" suffix="h before" width={200} />
        <NumberField value={previewPaid} min={0} onChange={(v) => setPreviewPaid(v ?? 0)} prefix="Paid" width={150} />
        <NumberField value={previewTax} min={0} onChange={(v) => setPreviewTax(v ?? 0)} prefix="Tax" width={150} />
      </Space>
      <Typography.Paragraph style={{ marginTop: 8 }}>
        Fee {preview.feePercent}% = <b>{preview.fee}</b> - customer refunded <b>{preview.refund}</b>
      </Typography.Paragraph>
    </Modal>
  );
};

export default CancellationPolicyEditor;
